import { FilterIcon, XIcon } from "lucide-react"
import type * as React from "react"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { CustomerPicker } from "@/features/invoices/DocumentPickers"
import { useCustomer } from "@/features/customers/use-customer"
import type { useQuotationFilters } from "@/features/quotations/use-quotation-list"
import { Badge } from "@/components/ui/badge"
import { DateField } from "@/components/DateField"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Label } from "@/components/ui/label"
import { RupeeBound } from "@/components/RupeeBound"
import { useFormatDay } from "@/lib/dates"
import { DATE_PRESETS, activeDatePreset } from "@/lib/date-presets"
import { formatRupees } from "@/lib/money"

/**
 * The quotation register's filters, behind one button (§7, §11.1).
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE SAME SHEET AS THE BILL REGISTER, MINUS THE ONE FILTER THIS ENDPOINT DOES
 * NOT HAVE. `QuotationListQuerySchema` is `.strict()` and declares `q`, `sort`,
 * `dir`, `status`, `customerId`, `from`, `to`, `minTotalPaise`, `maxTotalPaise`,
 * `limit` and `offset` — nothing else. So there is no hand-written flag here:
 * `issuedManually` is §6.1 and records that a TAX INVOICE was written by hand in
 * the owner's book and its number back-filled into the gap-free series. A
 * quotation has no such paper series, `issued_manually` is a column on `model
 * Invoice` alone, and a control for it here would answer 400 rather than being
 * politely ignored.
 *
 * THE AMOUNT RANGE FILTERS THE FIGURE ON THE SCREEN (§9.3), which on this
 * register is NOT `grandTotalPaise`. A quotation row prints
 * `display.printedGrandTotalPaise` — the PRE-TAX subtotal when tax is extra,
 * which is the default and the majority, and the tax-inclusive grand total
 * otherwise — so the SERVER matches these bounds against that same figure. A row
 * reading "₹6,500.00 + tax extra" is therefore inside "at most ₹6,500" and
 * outside "at least ₹7,000", which is what the sheet says on screen and what the
 * customer was told. The "+ tax extra" note on the rows themselves is what stops
 * the filtered figures reading as tax-inclusive; do not remove it.
 *
 * WHAT IS IN THE SHEET vs WHAT STAYS IN THE TOOLBAR. Status and the number
 * search stay outside, because those are what the owner reaches for; the
 * customer and the date range are a tap away, which keeps the toolbar one row on
 * a phone instead of three rows of date buttons.
 *
 * "SEARCH BY NAME" IS A CUSTOMER PICKER, NOT A TEXT BOX. `q` searches the
 * quotation's own numbers, never the buyer — the customer lives in
 * `customer_snapshot`, a text-backed JSON column the server will not query
 * inside (CLAUDE.md). The picker searches customers server-side and filters by
 * the `customerId` it returns, which covers the WHOLE book rather than the fifty
 * rows already on screen.
 *
 * THE DATE RANGE IS THE QUOTATION DATE (`quote_date`), not the validity date and
 * not the date a bill was raised from it. Presets come from `lib/date-presets`,
 * shared with the bill register so "This year" cannot come to mean two things.
 * ─────────────────────────────────────────────────────────────────────────────
 */
export function QuotationFilterSheet({
  filters,
  setFilters,
  clearFilters,
  activeCount,
}: {
  filters: ReturnType<typeof useQuotationFilters>["filters"]
  setFilters: ReturnType<typeof useQuotationFilters>["setFilters"]
  clearFilters: () => void
  activeCount: number
}): React.JSX.Element {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [pickingCustomer, setPickingCustomer] = useState(false)

  /*
   * The URL holds only the id, so the NAME has to be fetched to show it. Without
   * this the button would read "Customer #4", which is the database's way of
   * identifying Lupin, not the owner's.
   */
  const chosen = useCustomer(filters.customerId === undefined ? undefined : String(filters.customerId))

  const activePreset = activeDatePreset(filters.from, filters.to)

  return (
    <>
      <Button
        type="button"
        variant="outline"
        onClick={() => {
          setOpen(true)
        }}
      >
        <FilterIcon />
        {/* The count is the whole point of collapsing them: a filtered register
          * that LOOKS unfiltered is how someone concludes their quotations are
          * gone. */}
        {activeCount === 0
          ? t("list.moreFilters")
          : t("list.moreFiltersCount", { n: activeCount })}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="flex max-h-[85vh] flex-col gap-4 overflow-y-auto sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t("list.moreFilters")}</DialogTitle>
            <DialogDescription>{t("quotation.listSubtitle")}</DialogDescription>
          </DialogHeader>

          {/* ── Customer ─────────────────────────────────────────────────── */}
          <div className="flex flex-col gap-1.5">
            <Label>{t("lbl.customer")}</Label>
            {filters.customerId === undefined ? (
              <Button
                type="button"
                variant="outline"
                className="justify-start"
                onClick={() => {
                  setPickingCustomer(true)
                }}
              >
                {t("list.filterByCustomer")}
              </Button>
            ) : (
              <div className="flex items-center justify-between gap-2 rounded-md border p-2">
                <span className="min-w-0 truncate text-sm">
                  {chosen.data?.name ?? t("msg.loading")}
                </span>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => {
                    setFilters({ customerId: undefined })
                  }}
                >
                  <XIcon className="size-4" />
                  <span className="sr-only">{t("list.clearCustomerFilter")}</span>
                </Button>
              </div>
            )}
          </div>

          {/* ── Quotation date ───────────────────────────────────────────── */}
          <div className="flex flex-col gap-2">
            <Label>{t("list.quotationDate")}</Label>

            <div className="flex flex-wrap gap-2">
              {DATE_PRESETS.map((preset) => (
                <Button
                  key={preset.id}
                  type="button"
                  variant={activePreset?.id === preset.id ? "default" : "outline"}
                  onClick={() => {
                    setFilters(preset.range())
                  }}
                >
                  {t(preset.labelKey)}
                </Button>
              ))}
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="quotation-filter-from" className="text-muted-foreground text-xs">
                  {t("list.dateFrom")}
                </Label>
                {/* The app's own calendar, never `<input type="date">` — that
                  * renders in the BROWSER's locale, so a Marathi screen opened
                  * an English picker (§11.4). */}
                <DateField
                  id="quotation-filter-from"
                  clearable
                  value={filters.from ?? ""}
                  placeholder={t("list.dateFrom")}
                  onChange={(value) => {
                    setFilters({ from: value === "" ? undefined : value })
                  }}
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="quotation-filter-to" className="text-muted-foreground text-xs">
                  {t("list.dateTo")}
                </Label>
                <DateField
                  id="quotation-filter-to"
                  clearable
                  value={filters.to ?? ""}
                  placeholder={t("list.dateTo")}
                  onChange={(value) => {
                    setFilters({ to: value === "" ? undefined : value })
                  }}
                />
              </div>
            </div>
          </div>

          {/* ── Amount ───────────────────────────────────────────────────── */}
          {/* The same control as the bill sheet, from `components/RupeeBound`:
            * the owner types rupees, the URL and the API carry integer paise, and
            * an empty box is "no filter" rather than zero. What the bounds are
            * compared AGAINST differs — see the header — but that is the server's
            * job, and it is the server's job precisely so the two registers
            * cannot come to disagree about it. */}
          <div className="flex flex-col gap-1.5">
            <Label>{t("list.amountRange")}</Label>
            <div className="grid grid-cols-2 gap-2">
              <RupeeBound
                id="quotation-filter-min-amount"
                label={t("list.amountFrom")}
                paise={filters.minTotalPaise}
                onChange={(minTotalPaise) => {
                  setFilters({ minTotalPaise })
                }}
              />
              <RupeeBound
                id="quotation-filter-max-amount"
                label={t("list.amountTo")}
                paise={filters.maxTotalPaise}
                onChange={(maxTotalPaise) => {
                  setFilters({ maxTotalPaise })
                }}
              />
            </div>
          </div>

          <DialogFooter className="gap-2 sm:justify-between">
            <Button
              type="button"
              variant="ghost"
              disabled={activeCount === 0}
              onClick={() => {
                clearFilters()
              }}
            >
              {t("list.clearFilters")}
            </Button>
            <Button
              type="button"
              onClick={() => {
                setOpen(false)
              }}
            >
              {t("btn.done")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <CustomerPicker
        open={pickingCustomer}
        onOpenChange={setPickingCustomer}
        onPick={(customer) => {
          setFilters({ customerId: customer.id })
        }}
      />
    </>
  )
}

/**
 * The filters currently on, as removable chips under the toolbar.
 *
 * The status is NOT a chip: it has its own visible select in the toolbar, and
 * repeating it here would let the owner clear it from one place while the other
 * still shows it set.
 */
export function ActiveQuotationFilterChips({
  filters,
  setFilters,
}: {
  filters: ReturnType<typeof useQuotationFilters>["filters"]
  setFilters: ReturnType<typeof useQuotationFilters>["setFilters"]
}): React.JSX.Element | null {
  const { t } = useTranslation()
  const formatDay = useFormatDay()
  const chosen = useCustomer(
    filters.customerId === undefined ? undefined : String(filters.customerId),
  )

  const chips: { id: string; label: string; clear: () => void }[] = []

  if (filters.customerId !== undefined) {
    chips.push({
      id: "customer",
      label: chosen.data?.name ?? t("msg.loading"),
      clear: () => {
        setFilters({ customerId: undefined })
      },
    })
  }
  if (filters.from !== undefined || filters.to !== undefined) {
    chips.push({
      id: "dates",
      /* Through `formatDay`, so the chip reads "1 Aug 2026" like every other
       * date in the app. A raw `2026-08-01` here is the wire format leaking
       * onto the screen, and it reads as a different date to a person than the
       * rows it is filtering. */
      label: `${filters.from === undefined ? "…" : formatDay(filters.from)} → ${
        filters.to === undefined ? "…" : formatDay(filters.to)
      }`,
      clear: () => {
        setFilters({ from: undefined, to: undefined })
      },
    })
  }
  if (filters.minTotalPaise !== undefined || filters.maxTotalPaise !== undefined) {
    chips.push({
      id: "amount",
      /* Through `formatRupees`, so the chip reads "₹6,500.00" with Indian digit
       * grouping like every other amount in the app — never `toLocaleString`,
       * and never the raw paise integer, which is the wire format leaking onto
       * the screen as a number a hundred times too big. */
      label: `${
        filters.minTotalPaise === undefined ? "…" : formatRupees(filters.minTotalPaise)
      } → ${filters.maxTotalPaise === undefined ? "…" : formatRupees(filters.maxTotalPaise)}`,
      /* BOTH ENDS COME OFF TOGETHER. The chip shows one range, so clearing it
       * must leave no half-range behind narrowing the register invisibly. */
      clear: () => {
        setFilters({ minTotalPaise: undefined, maxTotalPaise: undefined })
      },
    })
  }

  if (chips.length === 0) return null

  return (
    <div className="flex flex-wrap gap-2">
      {chips.map((chip) => (
        /*
         * Each chip is REMOVABLE where it is shown. Sending someone back into
         * the dialog to undo one filter is the reason people give up and clear
         * everything, losing the two they wanted.
         */
        <Badge key={chip.id} variant="secondary" className="gap-1 py-1 pr-1 pl-2">
          <span className="max-w-48 truncate">{chip.label}</span>
          <button
            type="button"
            onClick={chip.clear}
            aria-label={t("list.clearFilters")}
            className="hover:bg-background/60 rounded-sm p-0.5"
          >
            <XIcon className="size-3" />
          </button>
        </Badge>
      ))}
    </div>
  )
}
